import { useNavigate } from "react-router-dom";
import { useMutation, useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { User, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SignOutDocument } from "@/graphql/signOut.api";

interface UserDropdownMenuProps {
  fullName?: string | null;
  email: string;
  initials?: string;
}

export function UserDropdownMenu({ fullName, email, initials }: UserDropdownMenuProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const client = useApolloClient();
  const [signOut] = useMutation(SignOutDocument);

  const handleSignOut = async () => {
    await signOut();
    await client.clearStore();
    navigate("/login");
  };

  const displayInitials = initials || (fullName ? fullName.charAt(0).toUpperCase() : "U");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-full text-sm font-bold outline-none hover:bg-emerald-100 transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-emerald-400">
          {displayInitials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            {fullName && <p className="text-sm font-medium leading-none">{fullName}</p>}
            <p className="text-xs leading-none text-muted-foreground">{email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
          <User className="mr-2 h-4 w-4" />
          {t("userMenu.profile")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600 focus:text-red-600">
          <LogOut className="mr-2 h-4 w-4" />
          {t("userMenu.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
